import React from 'react';
import { Label } from '../../../ui/label';
import { Input } from '../../../ui/input';
import { Button } from '../../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../ui/dialog';

const selectClass =
  'mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500';

/**
 * Add / Edit dialog for a Material Sales Data combination.
 */
const SalesDataDialog = ({
  open,
  onOpenChange,
  editingIndex,
  currentSalesData,
  setCurrentSalesData,
  masterData,
  onSave,
}) => {
  const {
    salesOrgs, unitsOfMeasure, accountAssignmentGroups, availabilityChecks,
    profitCenters, transportationGroups, loadingGroups, plants, getAvailableDistChannels,
  } = masterData;

  const set = (patch) => setCurrentSalesData({ ...currentSalesData, ...patch });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="sales-data-dialog">
        <DialogHeader>
          <DialogTitle>{editingIndex !== null ? 'Edit Sales Data' : 'Add Sales Data'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Organization Selection */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-orange-900 mb-3">Organization Selection</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Sales Organization *</Label>
                <select
                  data-testid="sales-data-sales-org"
                  className={selectClass}
                  value={currentSalesData.sales_org_id}
                  onChange={(e) => set({ sales_org_id: e.target.value, dist_channel_id: '' })}
                >
                  <option value="">Select Sales Organization</option>
                  {salesOrgs.map((so) => (
                    <option key={so.sales_org_id} value={so.sales_org_id}>
                      {so.sales_org_id} - {so.sales_org_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Distribution Channel *</Label>
                <select
                  data-testid="sales-data-dist-channel"
                  className={selectClass}
                  value={currentSalesData.dist_channel_id}
                  onChange={(e) => set({ dist_channel_id: e.target.value })}
                  disabled={!currentSalesData.sales_org_id}
                >
                  <option value="">
                    {currentSalesData.sales_org_id ? 'Select Distribution Channel' : 'Select Sales Org First'}
                  </option>
                  {getAvailableDistChannels(currentSalesData.sales_org_id).map((dc) => (
                    <option key={dc.dist_channel_id} value={dc.dist_channel_id}>
                      {dc.dist_channel_id} - {dc.dist_channel_name}
                    </option>
                  ))}
                </select>
                {currentSalesData.sales_org_id &&
                  getAvailableDistChannels(currentSalesData.sales_org_id).length === 0 && (
                    <p className="text-xs text-red-600 mt-1">No distribution channels assigned to this sales organization</p>
                  )}
              </div>
              <div>
                <Label>Plant *</Label>
                <select
                  data-testid="sales-data-plant"
                  className={selectClass}
                  value={currentSalesData.plant_id}
                  onChange={(e) => set({ plant_id: e.target.value })}
                >
                  <option value="">Select Plant</option>
                  {plants.map((p) => (
                    <option key={p.plant_id} value={p.plant_id}>
                      {p.plant_id} - {p.plant_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Sales Organization Data */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Sales Organization Data</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Sales Unit *</Label>
                <select
                  data-testid="sales-data-sales-unit"
                  className={selectClass}
                  value={currentSalesData.sales_unit}
                  onChange={(e) => set({ sales_unit: e.target.value })}
                >
                  <option value="">Select Sales Unit</option>
                  {unitsOfMeasure.map((uom) => (
                    <option key={uom.unit_code} value={uom.unit_code}>
                      {uom.unit_code} - {uom.description}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Unit in which the material is sold</p>
              </div>
              <div>
                <Label>Minimum Order Quantity</Label>
                <Input
                  type="number" min="0" step="0.001"
                  value={currentSalesData.min_order_qty}
                  onChange={(e) => set({ min_order_qty: e.target.value })}
                  placeholder="Optional"
                />
              </div>
              <div>
                <Label>Minimum Delivery Quantity</Label>
                <Input
                  type="number" min="0" step="0.001"
                  value={currentSalesData.min_delivery_qty}
                  onChange={(e) => set({ min_delivery_qty: e.target.value })}
                  placeholder="Optional"
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  id="cash_discount"
                  checked={currentSalesData.cash_discount}
                  onChange={(e) => set({ cash_discount: e.target.checked })}
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                />
                <label htmlFor="cash_discount" className="text-sm font-medium text-gray-700">
                  Cash Discount Eligible
                </label>
              </div>
            </div>
          </div>

          {/* Account Assignment */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Account Assignment</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Account Assignment Group</Label>
                <select
                  data-testid="sales-data-aag"
                  className={selectClass}
                  value={currentSalesData.account_assignment_group}
                  onChange={(e) => set({ account_assignment_group: e.target.value })}
                >
                  <option value="">Select Group (Optional)</option>
                  {accountAssignmentGroups.map((aag) => (
                    <option key={aag.account_assignment_group_id} value={aag.account_assignment_group_id}>
                      {aag.account_assignment_group_id} - {aag.description}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Controls account assignment in purchasing</p>
              </div>
            </div>
          </div>

          {/* Plant & Logistics Data */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Plant & Logistics Data</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Availability Check *</Label>
                <select
                  data-testid="sales-data-availability-check"
                  className={selectClass}
                  value={currentSalesData.availability_check}
                  onChange={(e) => set({ availability_check: e.target.value })}
                >
                  <option value="">Select Availability Check</option>
                  {availabilityChecks.map((ac) => (
                    <option key={ac.availability_check_id} value={ac.availability_check_id}>
                      {ac.availability_check_id} - {ac.description}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Controls ATP checking</p>
              </div>
              <div>
                <Label>Profit Center *</Label>
                <select
                  data-testid="sales-data-profit-center"
                  className={selectClass}
                  value={currentSalesData.profit_center}
                  onChange={(e) => set({ profit_center: e.target.value })}
                >
                  <option value="">Select Profit Center</option>
                  {profitCenters.map((pc) => (
                    <option key={pc.profit_center_id} value={pc.profit_center_id}>
                      {pc.profit_center_id} - {pc.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Profitability analysis area</p>
              </div>
              <div>
                <Label>Transportation Group *</Label>
                <select
                  data-testid="sales-data-transportation-group"
                  className={selectClass}
                  value={currentSalesData.transportation_group}
                  onChange={(e) => set({ transportation_group: e.target.value })}
                >
                  <option value="">Select Transportation Group</option>
                  {transportationGroups.map((tg) => (
                    <option key={tg.transportation_group_id} value={tg.transportation_group_id}>
                      {tg.transportation_group_id} - {tg.description}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Transportation requirements</p>
              </div>
              <div>
                <Label>Loading Group *</Label>
                <select
                  data-testid="sales-data-loading-group"
                  className={selectClass}
                  value={currentSalesData.loading_group}
                  onChange={(e) => set({ loading_group: e.target.value })}
                >
                  <option value="">Select Loading Group</option>
                  {loadingGroups.map((lg) => (
                    <option key={lg.loading_group_id} value={lg.loading_group_id}>
                      {lg.loading_group_id} - {lg.description}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Loading/unloading requirements</p>
              </div>
            </div>
          </div>

          {/* ATP Lead Times */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              ATP Lead Times (Shipping)
              <span className="text-xs font-normal text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                Important for Availability Check
              </span>
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              These fields are used in ATP calculation for delivery date confirmation. If not maintained, system defaults will be used.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Loading Time (Days)</Label>
                <Input
                  type="number" min="0" max="99"
                  value={currentSalesData.loading_time || ''}
                  onChange={(e) => set({ loading_time: e.target.value })}
                  placeholder="Default: 1 day"
                />
                <p className="text-xs text-gray-500 mt-1">Time required for loading goods onto transport</p>
              </div>
              <div>
                <Label>Pick/Pack Time (Days)</Label>
                <Input
                  type="number" min="0" max="99"
                  value={currentSalesData.pick_pack_time || ''}
                  onChange={(e) => set({ pick_pack_time: e.target.value })}
                  placeholder="Default: 1 day"
                />
                <p className="text-xs text-gray-500 mt-1">Time required for picking and packing</p>
              </div>
            </div>
          </div>
        </div>

        {/* Dialog Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="sales-data-cancel-btn">
            Cancel
          </Button>
          <Button onClick={onSave} className="bg-orange-600 hover:bg-orange-700" data-testid="sales-data-save-btn">
            {editingIndex !== null ? 'Update' : 'Add'} Sales Data
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SalesDataDialog;
